import { useFormikContext } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@material-ui/core';

import { getIcon } from 'app/components/ErplyIcon';
import PricePlanOption from 'app/components/PricePlanOption';
import CheckBox from 'app/components/CheckBox';
import {
  BaseForm,
  CheckFormContent,
  PricePlanFormContent,
} from 'app/pages/Registration/interfaces';
import baseForm from 'assets/baseForm';
import { IForm } from 'app/pages/Registration/utils';
import { CountriesKeys, supportedCountries } from 'service/countrisList';

import {
  ConfirmWrapper,
  Content,
  ExpandIcon,
  Fields,
  Icon,
  SubFieldLabel,
} from './styles';

const renderHardware = (label: string) => (
  <Typography key={label} className="hardware-list__item">
    {label}
  </Typography>
);
interface Props {
  children?: never;
  path: string;
  base: BaseForm[];
}

const Confirm: React.FC<Props> = ({ base }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<IForm>();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { options } = useMemo(
    () =>
      (baseForm
        .find((el) => el.path === 'pricePlan')
        ?.content.find((el) => el.type === 'pricePlan') ??
        []) as PricePlanFormContent,
    [],
  );
  const { plan } = values.pricePlan.pricePlan as {
    period: string;
    plan: number | null;
  };
  const {
    yearPrice = 1,
    monthPrice = 1,
    title = '',
    perks = [],
  } = (plan !== null && options[plan]) || {};
  const { native, phone } =
    supportedCountries[values.region.countrySelect as CountriesKeys];
  const softwareQuestions = Object.entries(values.softwareQuestions).filter(
    ([, val]) => val,
  );
  const desktopHardware =
    base
      .find((el) => el.path === 'desktopHardwareQuestions')
      ?.content.find(
        (el): el is CheckFormContent => el.label === 'needsDesktopHardware',
      )
      ?.options?.map((el) => el.label)
      .filter((el) => values.desktopHardwareQuestions[el])
      .map((el) => t(`registration.component.checkbox.${el}.label`)) ?? [];
  const mobileHardware =
    base
      .find((el) => el.path === 'mobileHardwareQuestions')
      ?.content.find(
        (el): el is CheckFormContent => el.label === 'needsMobileHardware',
      )
      ?.options?.map((el) => el.label)
      .filter((el) => values.mobileHardwareQuestions[el])
      .map((el) => t(`registration.component.checkbox.${el}.label`)) ?? [];

  return (
    <>
      <Fields
        $isOpen={isOpen}
        ref={ref}
        $height={ref.current?.scrollHeight ?? 0}
      >
        <div>
          <SubFieldLabel variant="body1">
            <Icon $icon={getIcon('Person-1')} />
            {t('registration.confirm.contactInfo')}
            <ExpandIcon
              size="small"
              $isOpen={isOpen}
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <span className="expand" />
            </ExpandIcon>
          </SubFieldLabel>
          <Content>
            <Typography className="field">
              {t('registration.component.inputField.firstName')}
            </Typography>
            <Typography className="value">
              {values.contactPerson.firstName} {values.contactPerson.lastName}
            </Typography>
            <Typography className="field">
              {t('registration.component.inputField.email')}
            </Typography>
            <Typography className="value">{values.email.email}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.phone')}
            </Typography>
            <Typography className="value">
              +{phone} {values.contactPerson.phone}
            </Typography>
            <Typography className="field">
              {t('registration.component.inputField.role')}
            </Typography>
            <Typography className="value">
              {values.contactPerson.role}
            </Typography>
            <Typography className="field">
              {t('registration.component.countrySelect')}
            </Typography>
            <Typography>{native}</Typography>
          </Content>
        </div>
        <div>
          <SubFieldLabel>
            <Icon $icon={getIcon('Office')} />
            {t('registration.confirm.companyDetails')}
          </SubFieldLabel>
          <Content>
            <Typography className="field">
              {t('registration.component.inputField.companyName')}
            </Typography>
            <Typography>{values.companyDetails.companyName}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.addressLine1')}
            </Typography>
            <Typography>{values.companyDetails.addressLine1}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.addressLine2')}
            </Typography>
            <Typography>{values.companyDetails.addressLine2}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.vatNumber')}
            </Typography>
            <Typography>{values.companyDetails.vatNumber}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.regCode')}
            </Typography>
            <Typography>{values.companyDetails.regCode}</Typography>
          </Content>
        </div>
        <div>
          <SubFieldLabel>
            <Icon $icon={getIcon('Number')} />
            {t('registration.confirm.companySize')}
          </SubFieldLabel>
          <Content>
            <Typography className="field">
              {t('registration.component.inputField.numberOfLocations')}
            </Typography>
            <Typography>{values.companyLocations.numberOfLocations}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.numberOfCashRegisters')}
            </Typography>
            <Typography>{values.companySize.numberOfCashRegisters}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.numberOfSystemUsers')}
            </Typography>
            <Typography>{values.companySize.numberOfSystemUsers}</Typography>
            <Typography className="field">
              {t('registration.component.inputField.numberOfProducts')}
            </Typography>
            <Typography>{values.companySize.numberOfProducts}</Typography>
          </Content>
        </div>
        {softwareQuestions.length > 0 && (
          <div>
            <SubFieldLabel>
              <Icon $icon={getIcon('Code')} />
              {t('registration.confirm.softwareQuestions')}
            </SubFieldLabel>
            {softwareQuestions.map(([key]) => (
              <Typography key={key} className="software-list__item">
                {t(`registration.component.checkbox.${key}.label`)}
              </Typography>
            ))}
          </div>
        )}
        {((values.mobileHardwareQuestions.needsMobileHardware as boolean) ||
          (values.desktopHardwareQuestions
            .needsDesktopHardware as boolean)) && (
          <div>
            <SubFieldLabel>
              <Icon $icon={getIcon('Print')} />
              {t('registration.confirm.hardwareQuestions')}
            </SubFieldLabel>
            {(values.mobileHardwareQuestions
              .needsMobileHardware as boolean) && (
              <>
                <Typography className="hardware-list__item">
                  {t(
                    'registration.component.checkbox.needsMobileHardware.label',
                  )}
                </Typography>
                {mobileHardware.map(renderHardware)}
              </>
            )}
            {(values.desktopHardwareQuestions
              .needsDesktopHardware as boolean) && (
              <>
                <Typography className="hardware-list__item">
                  {t(
                    'registration.component.checkbox.needsDesktopHardware.label',
                  )}
                </Typography>
                {desktopHardware.map(renderHardware)}
              </>
            )}
          </div>
        )}
      </Fields>
      <PricePlanOption
        yearPrice={yearPrice}
        perks={perks}
        monthPrice={monthPrice}
        period={values.pricePlan.period as 'annual' | 'monthly'}
        title={title}
        confirmView
      />
      <CheckBox
        path="confirm"
        label="confirm"
        labelNode={
          <ConfirmWrapper>
            <Link
              href="https://erply.com/terms-of-service/"
              target="_blank"
              rel="noreferrer"
            >
              {t('registration.component.inputField.confirm')}
            </Link>
          </ConfirmWrapper>
        }
      />
    </>
  );
};

export default Confirm;
